/**
 * Thank You Voucher Sales — the dedicated box on the Retail season panel, and
 * its PDF report.
 *
 * A Thank You voucher is the Gift Aid "DON" voucher a donating party receives
 * with their tickets, spent in the shops and tea rooms within the week. The
 * box totals, for the shown period, the sales made with them: the spend, how
 * many vouchers (one per party — the "people" figure), the voucher value, and
 * the over/under — topped up (spend above the voucher, paid by other means)
 * and unspent (value left on the voucher, which MERAC books as Voucher Overs).
 *
 * The PDF lists every transaction: date, time, outlet, items, spend, voucher
 * value, voucher used, over/under, with totals at the bottom.
 */
import type { TyvResponse, TyvTransaction } from "./salesInsightsTypes";
import { pounds, prettyIso, savePdf, sectionTitle, signedPounds, startPdf, table } from "./salesPdf";

export interface ThankYouVoucherBoxProps {
  data: TyvResponse | null;
  loading: boolean;
  error: string | null;
  rangeLabel: string;
  measure: "Net" | "Gross";
  fmt: (n: number) => string;
  onPdf: () => void;
  pdfBusy: boolean;
}

export function ThankYouVoucherBox(props: ThankYouVoucherBoxProps) {
  const { data, loading, error, rangeLabel, measure, fmt, onPdf, pdfBusy } = props;
  const s = data?.summary;
  const spend = s ? (measure === "Gross" ? s.spendGross : s.spendNet) : 0;
  const retail = data?.byCategory.find((c) => c.category === "Retail");
  const tea = data?.byCategory.find((c) => c.category === "Tea Room");
  const pick = (f: { spendGross: number; spendNet: number } | undefined) => (f ? (measure === "Gross" ? f.spendGross : f.spendNet) : 0);
  const empty = !loading && !error && (!s || s.transactions === 0);

  return (
    <div className="sales-tile sales-tile--gc sales-tile--tyv" title="Shop and tea-room sales paid with a Gift Aid Thank You voucher in the shown period">
      <div className="sales-gc-head">
        Thank You vouchers <span className="sales-gc-scope">{rangeLabel}</span>
      </div>
      {loading && !data ? (
        <div className="sales-gc-foot">Loading…</div>
      ) : error ? (
        <div className="sales-gc-foot sales-gc-foot--error">Couldn’t load Thank You voucher sales: {error}</div>
      ) : empty ? (
        <div className="sales-gc-foot">No sales paid with a Thank You voucher in this period.</div>
      ) : s ? (
        <>
          <div className="sales-gc-stats">
            <div className="sales-gc-stat sales-gc-stat--out">
              <span className="sales-gc-k">Sales ({measure})</span>
              <span className="sales-gc-v">{fmt(spend)}</span>
            </div>
            <div className="sales-gc-stat">
              <span className="sales-gc-k">People (vouchers)</span>
              <span className="sales-gc-v">{s.vouchers.toLocaleString()}</span>
            </div>
            <div className="sales-gc-stat">
              <span className="sales-gc-k">Voucher value</span>
              <span className="sales-gc-v">{fmt(s.voucherValue)}</span>
            </div>
          </div>
          <div className="sales-gc-stats">
            <div className="sales-gc-stat">
              <span className="sales-gc-k">Over — topped up</span>
              <span className="sales-gc-v sales-gc-v--plus">+{fmt(s.toppedUp)}</span>
            </div>
            <div className="sales-gc-stat">
              <span className="sales-gc-k">Under — unspent</span>
              <span className="sales-gc-v sales-gc-v--minus">−{fmt(s.unspent)}</span>
            </div>
            <div className="sales-gc-stat">
              <span className="sales-gc-k">Transactions</span>
              <span className="sales-gc-v">{s.transactions.toLocaleString()}</span>
            </div>
          </div>
          <div className="sales-gc-foot">
            Retail {fmt(pick(retail))} · Tea rooms {fmt(pick(tea))} · over/under = spend − voucher value; unspent value is kept by the railway
          </div>
        </>
      ) : null}
      <div className="sales-gc-actions">
        <button type="button" className="sales-gc-btn" onClick={onPdf} disabled={pdfBusy || loading || !s || s.transactions === 0}
          title="Download a PDF listing every Thank You voucher transaction in the period — date, item, spend, voucher used, over/under — with totals">
          {pdfBusy ? "Building PDF…" : "PDF report"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The PDF — one row per transaction, totals at the foot.
// ---------------------------------------------------------------------------
export function downloadTyvPdf(data: TyvResponse, ctx: { rangeLabel: string; seasonLabel: string }): void {
  const s = data.summary;
  const period = data.scope.from && data.scope.to ? `${prettyIso(data.scope.from)} to ${prettyIso(data.scope.to)}` : ctx.rangeLabel;
  const { doc, y: y0 } = startPdf(
    "Thank You Voucher Sales",
    `${period}  ·  season ${ctx.seasonLabel}  ·  ${s.transactions.toLocaleString()} transactions  ·  ${s.vouchers.toLocaleString()} vouchers (people)  ·  sales ${pounds(s.spendGross)} inc VAT / ${pounds(s.spendNet)} ex VAT`
  );
  let y = sectionTitle(doc, "Totals", y0);
  y = table(doc, {
    startY: y,
    head: [["", "Transactions", "Vouchers (people)", "Spend inc VAT", "Spend ex VAT", "Voucher value", "Voucher used", "Over — topped up", "Under — unspent"]],
    body: [
      ...data.byCategory.map((c) => [c.category ?? "", c.transactions.toLocaleString(), c.vouchers.toLocaleString(), pounds(c.spendGross), pounds(c.spendNet), pounds(c.voucherValue), pounds(c.voucherTendered), `+${pounds(c.toppedUp)}`, `-${pounds(c.unspent)}`]),
    ],
    foot: [["Total", s.transactions.toLocaleString(), s.vouchers.toLocaleString(), pounds(s.spendGross), pounds(s.spendNet), pounds(s.voucherValue), pounds(s.voucherTendered), `+${pounds(s.toppedUp)}`, `-${pounds(s.unspent)}`]],
    columnStyles: { 0: { halign: "left" } },
  });

  y = sectionTitle(doc, "By outlet", y);
  y = table(doc, {
    startY: y,
    head: [["Outlet", "Transactions", "Vouchers", "Spend inc VAT", "Spend ex VAT", "Voucher value", "Over — topped up", "Under — unspent"]],
    body: data.byLocation.map((l) => [`${l.location ?? ""} (${l.category ?? ""})`, l.transactions.toLocaleString(), l.vouchers.toLocaleString(), pounds(l.spendGross), pounds(l.spendNet), pounds(l.voucherValue), `+${pounds(l.toppedUp)}`, `-${pounds(l.unspent)}`]),
    columnStyles: { 0: { halign: "left" } },
  });

  const txns: TyvTransaction[] = data.transactions ?? [];
  y = sectionTitle(doc, `Every transaction (${txns.length.toLocaleString()})`, y);
  const tot = txns.reduce(
    (a, t) => ({ spend: a.spend + t.spendGross, value: a.value + t.voucherValue, used: a.used + t.voucherTendered, ou: a.ou + t.overUnder, v: a.v + t.vouchers }),
    { spend: 0, value: 0, used: 0, ou: 0, v: 0 }
  );
  table(doc, {
    startY: y,
    head: [["Date", "Time", "Outlet", "Items", "Total spend", "Voucher value", "Voucher used", "Over / under"]],
    body: txns.map((t) => [
      prettyIso(t.saleDate), t.time ?? "", t.location, t.itemsText ?? "", pounds(t.spendGross), pounds(t.voucherValue) + (t.vouchers > 1 ? ` (${t.vouchers})` : ""), pounds(t.voucherTendered), signedPounds(t.overUnder),
    ]),
    foot: [["Total", "", `${txns.length.toLocaleString()} transactions`, `${tot.v.toLocaleString()} vouchers`, pounds(tot.spend), pounds(tot.value), pounds(tot.used), signedPounds(tot.ou)]],
    columnStyles: { 0: { halign: "left", cellWidth: 20 }, 1: { halign: "left", cellWidth: 12 }, 2: { halign: "left", cellWidth: 34 }, 3: { halign: "left", cellWidth: 104 } },
    styles: { fontSize: 7.5, cellPadding: 1.2, overflow: "linebreak", halign: "right" },
  });
  const from = data.scope.from ?? "period";
  const to = data.scope.to ?? "";
  savePdf(doc, `thank-you-voucher-sales-${from}${to ? `_to_${to}` : ""}.pdf`);
}
